import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import React, { createContext, useCallback, useContext, useState } from 'react';

type Severity = 'success' | 'info' | 'warning' | 'error';

const ToastContext = createContext<(message: string, severity?: Severity) => void>(() => undefined);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toast, setToast] = useState<{ message: string; severity: Severity; key: number } | null>(
    null,
  );
  const show = useCallback((message: string, severity: Severity = 'success') => {
    setToast({ message, severity, key: Date.now() });
  }, []);
  return (
    <ToastContext.Provider value={show}>
      {children}
      <Snackbar
        key={toast?.key}
        open={Boolean(toast)}
        autoHideDuration={3500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={toast?.severity ?? 'success'}
          variant="filled"
          onClose={() => setToast(null)}
          sx={{ minWidth: 280 }}
        >
          {toast?.message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}

export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel = 'Confirm',
  onConfirm,
  onClose,
  requireComment,
  commentLabel = 'Comment',
  danger,
}: {
  open: boolean;
  title: string;
  body?: React.ReactNode;
  confirmLabel?: string;
  onConfirm: (comment: string) => void;
  onClose: () => void;
  requireComment?: boolean;
  commentLabel?: string;
  danger?: boolean;
}) {
  const [comment, setComment] = useState('');
  const close = () => {
    setComment('');
    onClose();
  };
  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{title}</DialogTitle>
      <DialogContent>
        {typeof body === 'string' ? <Typography variant="body2">{body}</Typography> : body}
        {requireComment !== undefined && (
          <TextField
            label={commentLabel}
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            fullWidth
            multiline
            minRows={2}
            sx={{ mt: 2 }}
            required={requireComment}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={close}>Cancel</Button>
        <Button
          variant="contained"
          color={danger ? 'error' : 'primary'}
          disabled={requireComment && comment.trim().length === 0}
          onClick={() => {
            onConfirm(comment.trim());
            setComment('');
          }}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
